import { css } from '@emotion/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm, Resolver } from 'react-hook-form';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { TargetAudience } from 'redux/schemas/models/mentoring-programs';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { setSaveStatus } from 'redux/actions/mentoring-programs';
import {
  createMentoringProgramRecruitment,
  deleteMentoringProgramRecruitment,
  updateMentoringProgramRecruitment,
} from 'redux/actions/mentoring-program-recruitments';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { convertDateTime } from 'athena/components/mentoring-program/home/participant/session-edit-mode/session-edit-mode';
import { overlayStyles } from 'athena/components/mentoring-program/program-configuration/program-setup/styles';
import NvDatePicker, { DatePickerType, getMomentDate } from 'shared/components/inputs/nv-datepicker';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvRadioButton from 'shared/components/inputs/nv-radio-button';
import { NvTooltip } from 'shared/components/nv-tooltip';
import { config } from '@config/pendo.config.json';
import RegistrationPage from './registration-page';
import {
  mentorRequirementsDefaultValue,
  mentorExpectationsDefaultValue,
  mentorBenefitsDefaultValue,
  menteeRequirementsDefaultValue,
  menteeExpectationsDefaultValue,
  menteeBenefitsDefaultValue,
  getDefaultRecruitmentValues,
} from './default-values';
import PageContents from './page-contents';


const datePickerStyles = css`
  .react-datepicker-wrapper {
    width: 100%;
  }
`;

type FormValue = {
  mentorRequirements: string;
  mentorExpectations: string;
  mentorBenefits: string;
  mentorIncludeRequirements: boolean;
  mentorIncludeExpectations: boolean;
  mentorIncludeBenefits: boolean;
  menteeRequirements: string;
  menteeExpectations: string;
  menteeBenefits: string;
  menteeIncludeRequirements: boolean;
  menteeIncludeExpectations: boolean;
  menteeIncludeBenefits: boolean;
  interestDeadline?: moment.Moment | string | null;
};

const RecruitmentForm = () => {
  const dispatch = useAppDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { mentoringProgram, timeZone } = useContext(MentoringProgramContext);
  const { id: programId,
    recruitments,
  } = mentoringProgram || {};

  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram?.id));

  const recruitmentEnabled = !!recruitments?.mentor || !!recruitments?.mentee;

  const currentRole = useMemo(() => {
    const hasMentor = !!recruitments?.mentor;
    const hasMentee = !!recruitments?.mentee;
    if (hasMentor && hasMentee) return TargetAudience.MENTOR_AND_MENTEE;
    if (hasMentee) return TargetAudience.MENTEE;
    return TargetAudience.MENTOR;
  }, [recruitments]);

  const validationSchema = yup.object().shape({
    mentorRequirements: yup.string(),
    mentorExpectations: yup.string(),
    mentorBenefits: yup.string(),
    mentorIncludeRequirements: yup.boolean(),
    mentorIncludeExpectations: yup.boolean(),
    mentorIncludeBenefits: yup.boolean(),
    menteeRequirements: yup.string(),
    menteeExpectations: yup.string(),
    menteeBenefits: yup.string(),
    menteeIncludeRequirements: yup.boolean(),
    menteeIncludeExpectations: yup.boolean(),
    menteeIncludeBenefits: yup.boolean(),
    interestDeadline: yup.mixed().nullable(),
  });

  const getDefaultValues = useCallback(() => {
    const interestDeadline = recruitments?.mentor?.interestDeadline ?? recruitments?.mentee?.interestDeadline;
    return {
      mentorRequirements: recruitments?.mentor?.requirements ?? mentorRequirementsDefaultValue,
      mentorExpectations: recruitments?.mentor?.expectations ?? mentorExpectationsDefaultValue,
      mentorBenefits: recruitments?.mentor?.benefits ?? mentorBenefitsDefaultValue,
      mentorIncludeRequirements: recruitments?.mentor?.includeRequirements ?? true,
      mentorIncludeExpectations: recruitments?.mentor?.includeExpectations ?? true,
      mentorIncludeBenefits: recruitments?.mentor?.includeBenefits ?? true,
      menteeRequirements: recruitments?.mentee?.requirements ?? menteeRequirementsDefaultValue,
      menteeExpectations: recruitments?.mentee?.expectations ?? menteeExpectationsDefaultValue,
      menteeBenefits: recruitments?.mentee?.benefits ?? menteeBenefitsDefaultValue,
      menteeIncludeRequirements: recruitments?.mentee?.includeRequirements ?? true,
      menteeIncludeExpectations: recruitments?.mentee?.includeExpectations ?? true,
      menteeIncludeBenefits: recruitments?.mentee?.includeBenefits ?? true,
      interestDeadline: interestDeadline
        ? getMomentDate(interestDeadline, 'L LT', timeZone?.ianaTimeZone)
        : null,
    };
  }, [recruitments, timeZone?.ianaTimeZone]);

  const rhfMethods = useForm<FormValue>({
    mode: 'onChange',
    defaultValues: getDefaultValues(),
    resolver: yupResolver(validationSchema) as Resolver<FormValue>,
  });

  const { reset } = rhfMethods;

  useEffect(() => {
    reset(getDefaultValues());
  }, [reset, getDefaultValues]);

  const handleDeadlineChange = (date) => {
    const active: { role: 'mentor' | 'mentee'; id: number }[] = [];
    if (recruitments?.mentor?.id) active.push({ role: 'mentor', id: recruitments.mentor.id });
    if (recruitments?.mentee?.id) active.push({ role: 'mentee', id: recruitments.mentee.id });
    if (!active.length) return;

    const interestDeadline = date
      ? convertDateTime({ date, timeZone: timeZone?.ianaTimeZone, convert: false })
      : '';

    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
    Promise.all(active.map(({ role, id }) => wrapThunkAction(
      dispatch(updateMentoringProgramRecruitment({
        programId,
        role,
        recruitment: { id, interestDeadline },
      })),
    )))
      .then(() => dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED })))
      .finally(() => {
        setTimeout(() => dispatch(setSaveStatus({ newStatus: null })), 3000);
      });
  };

  const label = recruitmentEnabled
    ? t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ARIA_LABEL.TURN_REGISTRATION_PAGE_OFF()
    : t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ARIA_LABEL.TURN_REGISTRATION_PAGE_ON();

  const handleConfirm = () => {
    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));

    const deleteOps = [];
    if (recruitments?.mentor) {
      deleteOps.push(wrapThunkAction(
        dispatch(deleteMentoringProgramRecruitment({
          programId,
          recruitment: recruitments.mentor,
          role: 'mentor',
        })),
      ));
    }
    if (recruitments?.mentee) {
      deleteOps.push(wrapThunkAction(
        dispatch(deleteMentoringProgramRecruitment({
          programId,
          recruitment: recruitments.mentee,
          role: 'mentee',
        })),
      ));
    }

    Promise.all(deleteOps)
      .then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      }).finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
        setShowConfirmation(false);
      });
  };

  const handleChange = () => {
    if (recruitmentEnabled) {
      setShowConfirmation(true);
    } else {
      dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
      wrapThunkAction(
        dispatch(
          createMentoringProgramRecruitment({
            programId,
            role: 'mentor',
            recruitment: getDefaultRecruitmentValues('mentor'),
          }),
        ),
      ).then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      }).finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
      });
    }
  };

  const handleRoleChange = (newRole: TargetAudience) => {
    const hasMentor = !!recruitments?.mentor;
    const hasMentee = !!recruitments?.mentee;
    const needsMentor = newRole === TargetAudience.MENTOR || newRole === TargetAudience.MENTOR_AND_MENTEE;
    const needsMentee = newRole === TargetAudience.MENTEE || newRole === TargetAudience.MENTOR_AND_MENTEE;

    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));

    const ops = [];
    if (needsMentor && !hasMentor) {
      ops.push(wrapThunkAction(dispatch(createMentoringProgramRecruitment({ programId, role: 'mentor', recruitment: getDefaultRecruitmentValues('mentor') }))));
    }
    if (!needsMentor && hasMentor) {
      ops.push(wrapThunkAction(dispatch(deleteMentoringProgramRecruitment({ programId, recruitment: recruitments.mentor, role: 'mentor' }))));
    }
    if (needsMentee && !hasMentee) {
      ops.push(wrapThunkAction(dispatch(createMentoringProgramRecruitment({ programId, role: 'mentee', recruitment: getDefaultRecruitmentValues('mentee') }))));
    }
    if (!needsMentee && hasMentee) {
      ops.push(wrapThunkAction(dispatch(deleteMentoringProgramRecruitment({ programId, recruitment: recruitments.mentee, role: 'mentee' }))));
    }

    Promise.all(ops)
      .then(() => dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED })))
      .finally(() => {
        setTimeout(() => dispatch(setSaveStatus({ newStatus: null })), 3000);
      });
  };

  const showMentorForm = recruitmentEnabled && (currentRole === TargetAudience.MENTOR || currentRole === TargetAudience.MENTOR_AND_MENTEE);
  const showMenteeForm = recruitmentEnabled && (currentRole === TargetAudience.MENTEE || currentRole === TargetAudience.MENTOR_AND_MENTEE);
  const isBoth = showMentorForm && showMenteeForm;

  return (
    <div>
      <FormProvider {...rhfMethods}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className='d-flex mb-2 align-items-center'>
            <div className='heading-5 mr-2'>{t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.TITLE()}</div>
            <NvPopover
              enabled
              show={showConfirmation}
              overlayStyles={overlayStyles}
              content={(
                <NvConfirmationPopover
                  onCancel={() => setShowConfirmation(false)}
                  onConfirm={handleConfirm}
                  header={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.WARNING.TITLE()}
                  message={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.WARNING.CONTENT()}
                  cancelBtnText={t.FORM.CANCEL()}
                  confirmBtnText={t.FORM.OK()}
                />
              )}
              rootClose
              onHide={() => setShowConfirmation(false)}
              className='d-inline-flex mw-100'
              preventOverflow
            >
              <NvTooltip preventOverflow={false} text={label}>
                <NvSwitch
                  checked={recruitmentEnabled}
                  onChange={handleChange}
                  enabledLabel={t.SHARED.ON().toUpperCase()}
                  disabledLabel={t.SHARED.OFF().toUpperCase()}
                  dataQa={config.pendo.athena.mentorshipProgram.settings.recruitment.enabledSwitch}
                  aria-label={label}
                />
              </NvTooltip>
            </NvPopover>
          </div>
          <div>{t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.DESCRIPTION()}</div>
          {recruitmentEnabled && (
            <div className='mb-6 pt-4'>
              <div className='semi-bold mb-2'>
                {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ROLE_SELECTOR.LABEL()}
              </div>
              <div className='d-flex'>
                <NvRadioButton
                  name='recruitmentRole'
                  value={TargetAudience.MENTOR}
                  className='mr-6'
                  label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ROLE_SELECTOR.MENTOR(mentoringProgramAliases)}
                  checked={currentRole === TargetAudience.MENTOR}
                  onChange={() => handleRoleChange(TargetAudience.MENTOR)}
                />
                <NvRadioButton
                  name='recruitmentRole'
                  value={TargetAudience.MENTEE}
                  className='mr-6'
                  label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ROLE_SELECTOR.MENTEE(mentoringProgramAliases)}
                  checked={currentRole === TargetAudience.MENTEE}
                  onChange={() => handleRoleChange(TargetAudience.MENTEE)}
                />
                <NvRadioButton
                  name='recruitmentRole'
                  value={TargetAudience.MENTOR_AND_MENTEE}
                  label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ROLE_SELECTOR.BOTH()}
                  checked={currentRole === TargetAudience.MENTOR_AND_MENTEE}
                  onChange={() => handleRoleChange(TargetAudience.MENTOR_AND_MENTEE)}
                />
              </div>
            </div>
          )}
          {recruitmentEnabled && (
            <div className='d-flex pb-6'>
              <div className='w-50'>
                <div className='d-flex w-100 semi-bold gray-2 pb-1'>
                  {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.RECRUITMENT_INTEREST_DEADLINE()}
                </div>
                <div className='d-flex w-100'>{t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.RECRUITMENT_INTEREST_DEADLINE_DESCRIPTION()}</div>
              </div>
              <div className='d-flex w-50 align-items-center pl-4'>
                <NvDatePicker
                  withForm
                  allowEmpty
                  name='interestDeadline'
                  className='w-100'
                  css={datePickerStyles}
                  type={DatePickerType.DATETIME}
                  placeholder={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.RECRUITMENT_INTEREST_DEADLINE_PLACEHOLDER()}
                  onChange={handleDeadlineChange}
                  placement='left'
                  dataQa={config.pendo.athena.mentorshipProgram.settings.recruitment.interestDeadlineDateTimePicker}
                />
              </div>
            </div>
          )}
          {(showMentorForm || showMenteeForm) && (
            <div className='p-4 border-gray-5-1 border-radius-2'>
              {isBoth && <PageContents />}
              {showMentorForm && (
                <div className={isBoth ? 'mt-4 pt-4 border-top border-gray-5' : ''}>
                  <RegistrationPage
                    role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR}
                    namePrefix='mentor'
                    isBoth={isBoth}
                  />
                </div>
              )}
              {showMenteeForm && (
                <div className={isBoth ? 'mt-4 pt-4 border-top border-gray-5' : ''}>
                  <RegistrationPage
                    role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE}
                    namePrefix='mentee'
                    isBoth={isBoth}
                  />
                </div>
              )}
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default RecruitmentForm;
